import React, { useState } from "react";
import PropTypes from "prop-types";
import submit from "submit-form";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { getDateToDisplay } from "../../../../utils/nutrition-helpers";
import {
  buildCreateMealPlanBody,
  deleteMealPlan,
  resolveMealPlanTitle,
} from "../../../../utils/meal-planner-helper";
import SvgIcon from "../../../../parts/SvgIcon";
import Dropdown from "../../../../parts/dropdown";
import { useRillContext } from "../../../../rill/RillReact/share/base";

const todayUtc = Number(moment().format("YYYYMMDD"));

// Per coach feedback on the redesign — a long plan history made the card
// list feel overwhelming, so only the first few show by default.
const VISIBLE_PLANS_LIMIT = 4;

// The dropdown only ever offers these two — creating a meal plan straight
// from a PDF isn't a thing (PDFs live at the athlete level via Guides, not
// per nutrition-plan — see the PDF Guides card instead).
const MEAL_PLAN_MODES = [
  { value: "simplified", titleKey: "simplifiedTitle", descKey: "simplifiedDescription" },
  { value: "detailed", titleKey: "detailedTitle", descKey: "detailedDescription" },
];

// Local to this tab's card list only — Progress's own plan-status bar
// (planStatus.js / PlanStatusList.js) folds "has a meal plan" into its
// 4-state enum (ACTIVE/ACTIVE_MEAL/DUE/NONE); this redesign shows that as
// its own separate badge in the meal-plan column instead, so the lifecycle
// badge here only ever needs these 3 plain, date-only states. Kept out of
// the shared nutrition-progress module on purpose so Progress's behavior
// can't shift by touching this file.
const planCardStatus = (plan) => {
  if (plan.utcStartDate > todayUtc) return "upcoming";
  if (plan.utcEndDate < todayUtc) return "expired";
  return "active";
};

// Only the nutrition-plan side (.np-plans__col--plan) selects the plan on
// click — the whole meal-plan column (.np-plans__col--meal) opts out
// entirely, even though its contents (the meal-link div, the empty-state
// text) aren't otherwise-interactive elements that this check would catch.
// Within the nutrition-plan side, copy/edit/delete icon buttons and the
// add-meal-plan dropdown's summary trigger still need to do their own thing
// instead — same pattern as LogGrid's day cards: native click bubbling means
// the row's handler sees every click regardless of where it started, so we
// just check what was actually clicked. Deliberately NOT matching on
// `button`, since .np-plans__plan-info (the date/macros button) is the
// row's own pre-existing click target and must keep selecting the plan via
// this same bubbled handler, for both mouse and keyboard (Enter/Space on it
// dispatches a real click that bubbles here too).
const isOtherInteractiveClick = (e) =>
  e.target.closest(
    '.np-plans__col--meal, a, [role="button"], input, select, textarea, summary'
  ) != null;

const DayMacros = ({ calories, proteins, carbs, fats, fibers }) => (
  <div className="np-plans__plan-macros">
    kCal {Math.round(calories)} | P {Math.round(proteins)}g | C {Math.round(carbs)}g | F{" "}
    {Math.round(fats)}g
    {fibers > 0 ? ` | Fib ${Math.round(fibers)}g` : ""}
  </div>
);

DayMacros.propTypes = {
  calories: PropTypes.number,
  proteins: PropTypes.number,
  carbs: PropTypes.number,
  fats: PropTypes.number,
  fibers: PropTypes.number,
};

const PlanRow = ({ plan, selected, onSelect, onDelete, onDeleteMealPlan, onCreateMealPlan }) => {
  const { t } = useTranslation("athlete-portal");
  const { _id, utcStartDate, utcEndDate, restDay } = plan;
  // `= {}` alone only covers mealPlan being undefined — an explicitly null
  // mealPlan (unpopulated reference) skipped it and crashed the whole row.
  const { _id: mealPlanId = null, published: publishedMealPlan = null } =
    plan.mealPlan || {};

  const mealPlanStatusText = t(
    publishedMealPlan
      ? "nutritionPage.existingPlans.published"
      : "nutritionPage.existingPlans.draft"
  );
  const mealPlanTitle = resolveMealPlanTitle(plan.mealPlan, t);
  const mealPlanHref = `/actions/refresh?section=meal-planner&plan=${_id}&mealPlan=${mealPlanId}`;

  const onRowClick = (e) => {
    if (isOtherInteractiveClick(e)) return;
    onSelect();
  };

  const status = planCardStatus(plan);
  const statusLabelKey = {
    active: "activePlan",
    upcoming: "upcomingPlan",
    expired: "expiredPlan",
  }[status];

  return (
    // Mouse-only convenience: the nested .np-plans__plan-info button already
    // gives keyboard/screen-reader users a real, accessible way to select
    // this plan, so this row-level onClick just widens the click target to
    // the whole card without duplicating that as a second focusable control.
    // onRowClick below ignores clicks that actually land on the icon
    // buttons, the meal-plan link, or the add-meal-plan dropdown.
    // biome-ignore lint/a11y/noStaticElementInteractions: row-wide click is mouse-only; keyboard access is via the real nested button
    // biome-ignore lint/a11y/useKeyWithClickEvents: same as above — no separate key handler needed alongside the nested button
    <div //NOSONAR
      className={`np-plans__row np-plans__row--${status}${
        selected ? " np-plans__row--selected" : ""
      }`}
      onClick={onRowClick}
    >
      <div className="np-plans__col np-plans__col--plan">
        <button type="button" className="np-plans__plan-info">
          <div className="np-plans__plan-dates-row">
            <span className="np-plans__plan-dates">
              {getDateToDisplay(utcStartDate)} - {getDateToDisplay(utcEndDate)}
            </span>
            <span className={`np-plans__status-chip np-plans__status-chip--${status}`}>
              {t(`nutritionPage.existingPlans.${statusLabelKey}`)}
            </span>
          </div>
          {restDay ? (
            <div className="np-plans__day-macros-split">
              <div className="np-plans__day-macros-row">
                <span className="np-plans__day-macros-label">
                  {t("nutritionPage.existingPlans.trainingDay")}
                </span>
                <DayMacros {...plan} />
              </div>
              <div className="np-plans__day-macros-row">
                <span className="np-plans__day-macros-label">
                  {t("nutritionPage.existingPlans.restDay")}
                </span>
                <DayMacros {...restDay} />
              </div>
            </div>
          ) : (
            <DayMacros {...plan} />
          )}
        </button>

        <div className="np-plans__controls">
          <a //NOSONAR
            role="button"
            data-button-type="secondary"
            className="np-plans__icon-btn"
            href={`/actions/refresh?modal[type]=create-nutrition-plan&plan=${_id}&copy=${true}`}
          >
            <SvgIcon
              src="/img/new-icons/duplicate.svg"
              useCurrentColor
              alt={t("nutritionPage.existingPlans.copyAlt")}
            />
          </a>
          <a //NOSONAR
            role="button"
            data-button-type="secondary"
            className="np-plans__icon-btn"
            href={`/actions/refresh?modal[type]=create-nutrition-plan&plan=${_id}`}
          >
            <SvgIcon
              src="/img/new-icons/edit-pencil.svg"
              useCurrentColor
              alt={t("nutritionPage.existingPlans.editAlt")}
            />
          </a>
          <a //NOSONAR
            role="button"
            data-button-type="secondary"
            className="np-plans__icon-btn np-plans__icon-btn--danger"
            href="#"
            onClick={(e) => onDelete(e, _id, mealPlanId)}
          >
            <SvgIcon
              src="/img/new-icons/red-trash.svg"
              alt={t("nutritionPage.existingPlans.deleteAlt")}
              noFilterOnLight={true}
            />
          </a>
        </div>
      </div>

      <div className="np-plans__col np-plans__col--meal">
        {mealPlanId ? (
          <div className="np-plans__meal-link">
            <div className="np-plans__meal-header">
              <span className="np-plans__meal-title">{mealPlanTitle}</span>
              <a className="np-plans__meal-view" href={mealPlanHref}>
                {t("nutritionPage.existingPlans.viewMealPlan")}
                <SvgIcon src="/img/svg/icon_arrow-right.svg" useCurrentColor />
              </a>
            </div>
            <div className="np-plans__meal-footer">
              <span
                className={`np-plans__meal-badge np-plans__meal-badge--${
                  publishedMealPlan ? "published" : "draft"
                }`}
              >
                {mealPlanStatusText}
              </span>
              <div className="np-plans__meal-controls">
                <a //NOSONAR
                  role="button"
                  data-button-type="secondary"
                  className="np-plans__icon-btn"
                  href={mealPlanHref}
                >
                  <SvgIcon
                    src="/img/new-icons/edit-pencil.svg"
                    useCurrentColor
                    alt={t("nutritionPage.existingPlans.editAlt")}
                  />
                </a>
                <a //NOSONAR
                  role="button"
                  data-button-type="secondary"
                  className="np-plans__icon-btn np-plans__icon-btn--danger"
                  href="#"
                  onClick={(e) => onDeleteMealPlan(e, mealPlanId, _id)}
                >
                  <SvgIcon
                    src="/img/new-icons/red-trash.svg"
                    alt={t("nutritionPage.existingPlans.deleteAlt")}
                    noFilterOnLight={true}
                  />
                </a>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="np-plans__meal-empty">
              <p className="np-plans__meal-empty-title">
                {t("nutritionPage.existingPlans.noMealPlanTitle")}
              </p>
              <p className="np-plans__meal-empty-desc">
                {t("nutritionPage.existingPlans.noMealPlanDescription")}
              </p>
            </div>
            <div className="np-plans__meal-add">
              <Dropdown
                alignRight
                summaryText={
                  <span className="np-plans__add-label">
                    <SvgIcon src="/img/new-icons/add.svg" useCurrentColor />
                    {t("nutritionPage.existingPlans.addMealPlan")}
                  </span>
                }
                options={MEAL_PLAN_MODES.map(({ value, titleKey, descKey }) => ({
                  value,
                  text: (
                    <div className="np-plans__meal-option">
                      <span className="np-plans__meal-option-title">
                        {t(`createMealPlanModal.${titleKey}`)}
                      </span>
                      <span className="np-plans__meal-option-desc">
                        {t(`createMealPlanModal.${descKey}`)}
                      </span>
                    </div>
                  ),
                }))}
                onSelectFn={(planMode) => onCreateMealPlan(planMode, plan)}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

PlanRow.propTypes = {
  plan: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func,
  onDelete: PropTypes.func,
  onDeleteMealPlan: PropTypes.func,
  onCreateMealPlan: PropTypes.func,
};

const ExistingPlans = ({ nutritionPlans, handleChangeActivePlan, activePlanId }) => {
  const { t } = useTranslation("athlete-portal");
  const { locals } = useRillContext();
  const [showAllPlans, setShowAllPlans] = useState(false);
  const visiblePlans = showAllPlans
    ? nutritionPlans
    : nutritionPlans.slice(0, VISIBLE_PLANS_LIMIT);
  const clientWord = locals.activeUser.referAsClients
    ? t("nutritionPage.existingPlans.clients")
    : t("nutritionPage.existingPlans.athletes");
  const deleteNutritionPlan = (e, _id, mealPlanId) => {
    e.preventDefault();
    const proxyAthlete = locals.query.athlete;

    submit(
      `/actions/nutrition/delete-nutrition-plan/${_id}/${mealPlanId}?proxyAthlete=${proxyAthlete}`,
      {
        method: "GET",
      }
    );
  };

  // Same action MealPlannerHeader.js's own "More" menu delete option calls —
  // this is just a second entry point to it from the nutrition-plan list.
  const handleDeleteMealPlan = (e, mealPlanId, nutritionPlanId) => {
    e.preventDefault();
    deleteMealPlan({ mealPlanId, nutritionPlanId, athlete: locals.query.athlete, t });
  };

  // Same request CreateMealPlanModal.js's own two cards send — the dropdown
  // here replaces that modal as the trigger for this row, not the endpoint.
  const handleCreateMealPlan = (planMode, plan) => {
    const { activeUserToRequest, query } = locals;
    const body = buildCreateMealPlanBody({
      planMode,
      utcStartDate: plan.utcStartDate,
      utcEndDate: plan.utcEndDate,
      athleteId: plan.userId,
      coachId: activeUserToRequest._id,
      nutritionPlanId: plan._id,
    });
    submit(`/actions/meal-planner/create-meal-plan?proxyAthlete=${query.athlete}`, {
      method: "POST",
      body,
    });
  };

  return (
    <div className="np-plans">
      <div className="np-plans__header">
        <div>
          <p className="np-plans__title">
            {t("nutritionPage.existingPlans.activePlan")}
          </p>
          <p className="np-plans__subtitle">
            {t("nutritionPage.existingPlans.createPlansFor", {
              role: clientWord,
            })}
          </p>
        </div>
        <a //NOSONAR
          role="button"
          data-button-type="primary"
          data-icon
          className="np-plans__create"
          href={`/actions/refresh?modal[type]=create-nutrition-plan`}
        >
          <img
            src="/img/new-icons/add.svg"
            alt={t("nutritionPage.existingPlans.plusIconAlt")}
          />
          {t("nutritionPage.existingPlans.createNewPlan")}
        </a>
      </div>

      {nutritionPlans.length > 0 && (
        <div className="np-plans__columns">
          <span className="np-plans__column-label">
            {t("nutritionPage.existingPlans.nutritionPlanColumn")}
          </span>
          <span className="np-plans__column-label">
            {t("nutritionPage.existingPlans.mealPlan")}
          </span>
        </div>
      )}

      <div className="np-plans__rows">
        {nutritionPlans.length === 0 ? (
          <div className="np-plans__empty">
            <div className="np-plans__empty-icon-wrap">
              <SvgIcon
                src="/img/new-icons/clipboard-notes.svg"
                useCurrentColor
                classNames="np-plans__empty-icon"
              />
            </div>
            <p className="np-plans__empty-title">
              {t("nutritionPage.existingPlans.noPlansTitle")}
            </p>
            <p className="np-plans__empty-desc">
              {t("nutritionPage.existingPlans.noPlansDescription")}
            </p>
          </div>
        ) : (
          <>
            {visiblePlans.map((plan) => (
              <PlanRow
                key={plan._id}
                plan={plan}
                selected={plan._id === activePlanId}
                onSelect={() => handleChangeActivePlan(plan._id)}
                onDelete={deleteNutritionPlan}
                onDeleteMealPlan={handleDeleteMealPlan}
                onCreateMealPlan={handleCreateMealPlan}
              />
            ))}

            {/* Nested inside .np-plans__rows (not a sibling after it) so it
                lands right after the last row no matter how much extra
                stretched height items-stretch gives this card — any leftover
                space just becomes blank padding below the button, instead of
                pushing the button itself down to the bottom of the card. */}
            {!showAllPlans && nutritionPlans.length > VISIBLE_PLANS_LIMIT && (
              <a //NOSONAR
                role="button"
                data-button-type="secondary"
                className="np-plans__load-more"
                onClick={() => setShowAllPlans(true)}
              >
                {t("nutritionPage.existingPlans.loadMore")}
              </a>
            )}
          </>
        )}
      </div>
    </div>
  );
};

ExistingPlans.propTypes = {
  nutritionPlans: PropTypes.array,
  handleChangeActivePlan: PropTypes.func,
  activePlanId: PropTypes.string,
};

export default ExistingPlans;
